// QAQC Service - connects to Postgres via Azure Function
import { authService } from '../auth/authService';

class QAQCService {
  constructor() {
    // Use dedicated wolfwalker-api Function App (Python Flex Consumption with VNet)
    this.functionAppUrl = 'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
    
    // Azure Function endpoints for QAQC
    this.endpoints = {
      sampleCountReconcile: `${this.functionAppUrl}/api/sample_count_reconcile`,
      controlStandards: `${this.functionAppUrl}/api/control_standards_qc`,
      fieldDuplicates: `${this.functionAppUrl}/api/field_duplicates_qc`,
      blankAgFailures: `${this.functionAppUrl}/api/blank_ag_failures`,
      blankAuFailures: `${this.functionAppUrl}/api/blank_au_failures`,
      blankCuFailures: `${this.functionAppUrl}/api/blank_cu_failures`,
      qaqcFailures: `${this.functionAppUrl}/api/qaqc_failures`,
      refreshFailures: `${this.functionAppUrl}/api/refresh_failures`,
      
      // Bias/Drift Analysis Endpoints (matching actual Azure Function folder names)
      biasAlerts: `${this.functionAppUrl}/api/bias_alerts`,
      standardsDrift: `${this.functionAppUrl}/api/standards_drift`,
      standardsBiasWorkorder: `${this.functionAppUrl}/api/standards_bias_workorder`,
      standardsBiasMonth: `${this.functionAppUrl}/api/standards_bias_month`,
      blanksUnified: `${this.functionAppUrl}/api/blanks_unified`,
      blanksDrift: `${this.functionAppUrl}/api/blanks_drift`,
      blanksBiasWorkorder: `${this.functionAppUrl}/api/blanks_bias_workorder`,
      duplicatesDrift: `${this.functionAppUrl}/api/duplicates_drift`,
      duplicatesBiasWorkorder: `${this.functionAppUrl}/api/duplicates_bias_workorder`,
      
      // Optimized summary endpoint
      dashboardSummary: `${this.functionAppUrl}/api/dashboard_summary`,
      
      // Surrounding sample context endpoint
      surroundingSample: `${this.functionAppUrl}/api/surrounding_sample`,
      
      // Workorder notes endpoint
      workorderNotes: `${this.functionAppUrl}/api/workorder_notes`
    };
  }

  async getAuthenticatedHeaders() {
    try {
      const token = await authService.getAccessToken();
      const userInfo = authService.getUserInfo();
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userInfo?.email || userInfo?.username || 'unknown'
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  /**
   * Generic fetch helper for QAQC endpoints
   */
  async fetchEndpoint(url, options = {}) {
    try {
      const headers = await this.getAuthenticatedHeaders();
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        // Try to get detailed error from backend
        let errorDetails = { statusText: response.statusText };
        try {
          const errorBody = await response.json();
          errorDetails = errorBody;
          console.error('🔴 Backend Error Details:', {
            url,
            status: response.status,
            error: errorBody.error,
            message: errorBody.message,
            type: errorBody.type
          });
        } catch (e) {
          // If response body isn't JSON, just use statusText
          console.error('🔴 Backend Error:', response.status, response.statusText, 'from', url);
        }
        throw new Error(`Failed to fetch from ${url}: ${JSON.stringify(errorDetails)}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error(`❌ Error fetching from ${url}:`, error);
      return [];
    }
  }

  /**
   * Get sample count reconciliation data
   * Shows expected vs actual sample counts per workorder
   */
  async getSampleCountReconcile() {
    return await this.fetchEndpoint(this.endpoints.sampleCountReconcile);
  }

  /**
   * Get control standards QC data
   * Includes blanks and CRMs with z-scores and tolerance checks
   */
  async getControlStandards() {
    return await this.fetchEndpoint(this.endpoints.controlStandards);
  }

  /**
   * Get field duplicates QC data
   * RPD analysis with dynamic and static tolerance checks
   */
  async getFieldDuplicates() {
    return await this.fetchEndpoint(this.endpoints.fieldDuplicates);
  }

  /**
   * Get silver blank failures
   * Ag blank QC failures with z-scores and carryover percentages
   */
  async getBlankAgFailures() {
    return await this.fetchEndpoint(this.endpoints.blankAgFailures);
  }

  /**
   * Get gold blank failures
   * Au blank QC failures with z-scores and carryover percentages
   */
  async getBlankAuFailures() {
    return await this.fetchEndpoint(this.endpoints.blankAuFailures);
  }

  /**
   * Get copper blank failures
   * Cu blank QC failures with z-scores and carryover percentages
   */
  async getBlankCuFailures() {
    return await this.fetchEndpoint(this.endpoints.blankCuFailures);
  }

  /**
   * Get pre-calculated dashboard summary statistics
   * Optimized endpoint for overview page - returns aggregated counts only
   * Returns: Single object with all key metrics (unresolved failures, alerts, mismatches, etc.)
   */
  async getDashboardSummary() {
    const result = await this.fetchEndpoint(this.endpoints.dashboardSummary);
    // Returns a single object, not an array
    return result[0] || result;
  }

  /**
   * Get surrounding samples for a given sample_id
   * Returns samples before and after the target sample with depth and element values
   * Useful for investigating carryover effects and contextual quality patterns
   */
  async getSurroundingSamples(sampleId) {
    const url = `${this.endpoints.surroundingSample}?sample_id=${encodeURIComponent(sampleId)}`;
    return await this.fetchEndpoint(url);
  }

  /**
   * Get all workorder notes
   * Returns persistent user notes for workorder reconciliation tracking
   */
  async getWorkorderNotes() {
    return await this.fetchEndpoint(this.endpoints.workorderNotes);
  }

  /**
   * Get note for specific workorder
   * @param {string} workorderId - Workorder ID to get note for
   */
  async getWorkorderNote(workorderId) {
    const url = `${this.endpoints.workorderNotes}?workorder_id=${encodeURIComponent(workorderId)}`;
    return await this.fetchEndpoint(url);
  }

  /**
   * Save or update workorder note
   * @param {string} workorderId - Workorder ID
   * @param {string} note - Note text content
   */
  async saveWorkorderNote(workorderId, note) {
    try {
      const headers = await this.getAuthenticatedHeaders();
      
      const response = await fetch(this.endpoints.workorderNotes, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          workorder_id: workorderId, 
          note 
        })
      });

      if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(`Failed to save note: ${errorBody.message || response.statusText}`);
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('Error saving workorder note:', error);
      throw error;
    }
  }

  /**
   * Delete workorder note
   * @param {string} workorderId - Workorder ID to delete note for
   */
  async deleteWorkorderNote(workorderId) {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const url = `${this.endpoints.workorderNotes}?workorder_id=${encodeURIComponent(workorderId)}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(`Failed to delete note: ${errorBody.message || response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error deleting workorder note:', error);
      throw error;
    }
  }

  /**
   * Get all QAQC failures
   * Master failures table with investigation tracking
   */
  async getQAQCFailures() {
    return await this.fetchEndpoint(this.endpoints.qaqcFailures);
  }

  /**
   * Update failure resolution fields (resolved + resolution_comments)
   * @param {{ id: number|string, resolved?: boolean, resolutionComments?: string }} payload
   */
  async updateFailureResolution({ id, resolved, resolutionComments }) {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const body = { id };
      if (resolved !== undefined) body.resolved = resolved;
      if (resolutionComments !== undefined) body.resolution_comments = resolutionComments;

      const response = await fetch(this.endpoints.qaqcFailures, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let message = response.statusText;
        try {
          const errorBody = await response.json();
          message = errorBody.message || errorBody.error || message;
        } catch (_) {
          /* ignore */
        }
        throw new Error(`Failed to update failure resolution: ${message}`);
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('Error updating failure resolution:', error);
      throw error;
    }
  }

  /**
   * Refresh QAQC failures table
   * Triggers a refresh of the qaqc.failures table from all source views
   */
  async refreshFailures() {
    try {
      console.log('🔄 Triggering QAQC failures refresh...');
      const headers = await this.getAuthenticatedHeaders();
      
      const response = await fetch(this.endpoints.refreshFailures, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to refresh failures: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Failures refresh completed:', result);
      return result;
    } catch (error) {
      console.error('Error refreshing failures:', error);
      throw error;
    }
  }

  // ==================== BIAS/DRIFT ANALYSIS ENDPOINTS ====================

  /**
   * Get bias alerts
   * Unified alert feed across all QC domains (CONTROL_STANDARD, BLANK, DUPLICATE)
   */
  async getBiasAlerts() {
    return await this.fetchEndpoint(this.endpoints.biasAlerts);
  }

  /**
   * Get standards drift data
   * Per-insertion CRM stream for drift detection with EWMA and run-rule flags
   */
  async getStandardsDrift() {
    return await this.fetchEndpoint(this.endpoints.standardsDrift);
  }

  /**
   * Get standards bias per workorder
   * Bias summary per workorder by CRM code and element
   */
  async getStandardsBiasWorkorder() {
    return await this.fetchEndpoint(this.endpoints.standardsBiasWorkorder);
  }

  /**
   * Get standards bias per month
   * Long-term bias trends by month
   */
  async getStandardsBiasMonth() {
    return await this.fetchEndpoint(this.endpoints.standardsBiasMonth);
  }

  /**
   * Get unified blank results
   * Unified blank results for Au/Ag/Cu in one schema
   */
  async getBlanksUnified() {
    return await this.fetchEndpoint(this.endpoints.blanksUnified);
  }

  /**
   * Get blanks drift data
   * Blank drift with rolling statistics and carryover flags
   */
  async getBlanksDrift() {
    return await this.fetchEndpoint(this.endpoints.blanksDrift);
  }

  /**
   * Get blanks bias per workorder
   * Blank contamination summary per workorder
   */
  async getBlanksBiasWorkorder() {
    return await this.fetchEndpoint(this.endpoints.blanksBiasWorkorder);
  }

  /**
   * Get duplicates drift data
   * Duplicate precision tracking with RPD rolling statistics
   */
  async getDuplicatesDrift() {
    return await this.fetchEndpoint(this.endpoints.duplicatesDrift);
  }

  /**
   * Get duplicates bias per workorder
   * Precision summary per workorder
   */
  async getDuplicatesBiasWorkorder() {
    return await this.fetchEndpoint(this.endpoints.duplicatesBiasWorkorder);
  }

  /**
   * Load multiple datasets in parallel
   * @param {Array<string>} datasets - Array of dataset names
   * @returns {Object} Object with dataset names as keys
   */
  async loadDatasets(datasets) {
    console.log('🔄 Loading datasets:', datasets);
    
    const methodMap = {
      'qaqcFailures': this.getQAQCFailures,
      'biasAlerts': this.getBiasAlerts,
      'sampleCountReconcile': this.getSampleCountReconcile,
      'controlStandards': this.getControlStandards,
      'fieldDuplicates': this.getFieldDuplicates,
      'blankAgFailures': this.getBlankAgFailures,
      'blankAuFailures': this.getBlankAuFailures,
      'blankCuFailures': this.getBlankCuFailures,
      'standardsDrift': this.getStandardsDrift,
      'standardsBiasWorkorder': this.getStandardsBiasWorkorder,
      'standardsBiasMonth': this.getStandardsBiasMonth,
      'blanksDrift': this.getBlanksDrift,
      'duplicatesDrift': this.getDuplicatesDrift,
      'duplicatesBiasWorkorder': this.getDuplicatesBiasWorkorder,
      'blanksUnified': this.getBlanksUnified,
      'blanksBiasWorkorder': this.getBlanksBiasWorkorder
    };
    
    const promises = datasets.map(async (dataset) => {
      const method = methodMap[dataset];
      if (!method) {
        console.warn(`Unknown dataset: ${dataset}`);
        return { [dataset]: [] };
      }
      const data = await method.call(this);
      return { [dataset]: data };
    });
    
    const results = await Promise.all(promises);
    return Object.assign({}, ...results);
  }

  /**
   * @deprecated Use loadDatasets() or individual endpoint methods for lazy loading
   * Get all dashboard data for display (loads all 16 endpoints)
   */
  async getDashboardData() {
    try {
      console.warn('⚠️ getDashboardData loads all endpoints. Consider using lazy loading with loadDatasets() instead.');
      console.log('🔄 Fetching all QAQC dashboard data...');

      // Fetch all endpoints in parallel (original + bias endpoints)
      const [
        sampleCountReconcile,
        controlStandards,
        fieldDuplicates,
        blankAgFailures,
        blankAuFailures,
        blankCuFailures,
        qaqcFailures,
        biasAlerts,
        standardsDrift,
        standardsBiasWorkorder,
        standardsBiasMonth,
        blanksUnified,
        blanksDrift,
        blanksBiasWorkorder,
        duplicatesDrift,
        duplicatesBiasWorkorder
      ] = await Promise.all([
        this.getSampleCountReconcile(),
        this.getControlStandards(),
        this.getFieldDuplicates(),
        this.getBlankAgFailures(),
        this.getBlankAuFailures(),
        this.getBlankCuFailures(),
        this.getQAQCFailures(),
        this.getBiasAlerts(),
        this.getStandardsDrift(),
        this.getStandardsBiasWorkorder(),
        this.getStandardsBiasMonth(),
        this.getBlanksUnified(),
        this.getBlanksDrift(),
        this.getBlanksBiasWorkorder(),
        this.getDuplicatesDrift(),
        this.getDuplicatesBiasWorkorder()
      ]);

      console.log('✅ QAQC data loaded:', {
        sampleCountReconcile: sampleCountReconcile.length,
        controlStandards: controlStandards.length,
        fieldDuplicates: fieldDuplicates.length,
        blankAgFailures: blankAgFailures.length,
        blankAuFailures: blankAuFailures.length,
        blankCuFailures: blankCuFailures.length,
        qaqcFailures: qaqcFailures.length,
        biasAlerts: biasAlerts.length,
        standardsDrift: standardsDrift.length,
        standardsBiasWorkorder: standardsBiasWorkorder.length,
        standardsBiasMonth: standardsBiasMonth.length,
        blanksUnified: blanksUnified.length,
        blanksDrift: blanksDrift.length,
        blanksBiasWorkorder: blanksBiasWorkorder.length,
        duplicatesDrift: duplicatesDrift.length,
        duplicatesBiasWorkorder: duplicatesBiasWorkorder.length
      });

      return {
        sampleCountReconcile,
        controlStandards,
        fieldDuplicates,
        blankAgFailures,
        blankAuFailures,
        blankCuFailures,
        qaqcFailures,
        biasAlerts,
        standardsDrift,
        standardsBiasWorkorder,
        standardsBiasMonth,
        blanksUnified,
        blanksDrift,
        blanksBiasWorkorder,
        duplicatesDrift,
        duplicatesBiasWorkorder,
        success: true
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  /**
   * Filter data by search term
   */
  filterData(data, searchTerm) {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(item => 
      JSON.stringify(item).toLowerCase().includes(term)
    );
  }

  /**
   * Sort data by field and direction
   */
  sortData(data, field, direction = 'asc') {
    return [...data].sort((a, b) => {
      const aVal = a[field] ?? '';
      const bVal = b[field] ?? '';
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      if (direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }
}

// Export singleton instance
export const qaqcService = new QAQCService();
export default qaqcService;

